import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useMemo, useRef, useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { FETCH_SHIPMENT, SPLIT_SHIPMENT } from "./graphql";

function matchesProduct(row, searchText) {
  const query = searchText.trim().toLowerCase();
  return (
    !query ||
    row.itemNumber.toLowerCase().includes(query) ||
    row.productName.toLowerCase().includes(query)
  );
}

function ShipmentRows({
  shipment,
  selectedRowIds,
  setSelectedRowIds,
  assignedRowIds,
  searchText,
}) {
  function toggleRow(rowId) {
    setSelectedRowIds((current) =>
      current.includes(rowId)
        ? current.filter((id) => id !== rowId)
        : [...current, rowId],
    );
  }

  return (
    <div className="space-y-4">
      {shipment.shipmentInvoices.map((shipmentInvoice) => {
        const shipmentInvoiceRows = shipmentInvoice.shipmentInvoiceRows.filter(
          (row) => matchesProduct(row, searchText),
        );
        if (!shipmentInvoiceRows.length) return null;
        const sourceType =
          shipmentInvoice.invoice.odooModelType === "transfer" ? "INT" : "SO";

        return (
          <section
            key={shipmentInvoice.id}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
          >
            <div className="flex items-center justify-between bg-gray-100 px-4 py-3 dark:bg-gray-800">
              <div>
                {sourceType}{" "}
                <span className="font-bold">
                  {shipmentInvoice.invoice.number}
                </span>
              </div>
              <div className="text-sm opacity-70">
                {shipmentInvoice.shipmentInvoiceRows.length} products
              </div>
            </div>
            <div className="max-h-[70vh] divide-y divide-gray-100 overflow-y-auto dark:divide-gray-800">
              {shipmentInvoiceRows.map((row) => {
                const assigned = assignedRowIds.has(row.id);
                return (
                  <label
                    key={row.id}
                    className={`flex cursor-pointer items-center gap-3 px-4 py-3 font-normal transition-colors ${
                      assigned
                        ? "cursor-not-allowed opacity-40"
                        : "opacity-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRowIds.includes(row.id)}
                      disabled={assigned}
                      onChange={() => toggleRow(row.id)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-4 text-sm">
                        <span className="w-32 shrink-0 break-all font-bold">
                          {row.itemNumber}
                        </span>
                        <span className="min-w-0 break-words">
                          {row.productName}
                        </span>
                      </div>
                      <div className="mt-1 flex items-start gap-4 text-sm">
                        <span className="w-32 shrink-0">
                          <span className="font-bold">
                            {Number(row.totalCbm).toFixed(3)}
                          </span>{" "}
                          m³
                        </span>
                        <span>QTY {row.qty}</span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function SplitShipment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const nextTargetId = useRef(2);
  const [targets, setTargets] = useState([{ id: 1, rowIds: [] }]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [sourceSearchText, setSourceSearchText] = useState("");
  const [targetSearchTexts, setTargetSearchTexts] = useState({});
  const [expandedTargetIds, setExpandedTargetIds] = useState({});
  const { loading, error, data } = useQuery(FETCH_SHIPMENT, {
    variables: { id },
  });
  const [splitShipment, splitShipmentResult] = useMutation(SPLIT_SHIPMENT, {
    onCompleted: ({ splitShipment: result }) => {
      Alert("success", `${result.shipments.length} shipments created.`);
      navigate(`/shipment/shipments/${result.sourceShipment.id}`);
    },
    onError: (mutationError) => Alert("error", parseError(mutationError)),
  });

  const rows = useMemo(
    () =>
      data?.shipment.shipmentInvoices.flatMap((shipmentInvoice) =>
        shipmentInvoice.shipmentInvoiceRows.map((row) => ({
          ...row,
          shipmentInvoiceId: shipmentInvoice.id,
        })),
      ) || [],
    [data],
  );
  const rowById = useMemo(
    () => new Map(rows.map((row) => [row.id, row])),
    [rows],
  );
  const selectedCbm = useMemo(
    () =>
      selectedRowIds.reduce(
        (total, rowId) => total + Number(rowById.get(rowId)?.totalCbm || 0),
        0,
      ),
    [rowById, selectedRowIds],
  );
  const assignedRowIds = useMemo(
    () => new Set(targets.flatMap((target) => target.rowIds)),
    [targets],
  );
  const canSubmit =
    rows.length > 0 &&
    assignedRowIds.size === rows.length &&
    targets.every((target) => target.rowIds.length > 0);

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const { shipment } = data;
  const canSplit =
    shipment.isEffective &&
    !shipment.source &&
    shipment.children.edges.length === 0;

  function addTarget() {
    setTargets((current) => [
      ...current,
      { id: nextTargetId.current++, rowIds: [] },
    ]);
  }

  function removeTarget(targetId) {
    setTargets((current) => current.filter((target) => target.id !== targetId));
  }

  function assignSelectedRows(targetId) {
    if (!selectedRowIds.length) return;
    setTargets((current) =>
      current.map((target) => {
        const rowIds = target.rowIds.filter(
          (rowId) => !selectedRowIds.includes(rowId),
        );
        return target.id === targetId
          ? { ...target, rowIds: [...rowIds, ...selectedRowIds] }
          : { ...target, rowIds };
      }),
    );
    setSelectedRowIds([]);
  }

  function removeRow(targetId, rowId) {
    setTargets((current) =>
      current.map((target) =>
        target.id === targetId
          ? { ...target, rowIds: target.rowIds.filter((id) => id !== rowId) }
          : target,
      ),
    );
  }

  function toggleTargetRows(targetId) {
    setExpandedTargetIds((current) => ({
      ...current,
      [targetId]: !current[targetId],
    }));
  }

  function submit() {
    if (!canSubmit) return;
    splitShipment({
      variables: {
        shipmentId: shipment.id,
        targets: targets.map((target) => ({
          shipmentInvoiceRowIds: target.rowIds,
        })),
      },
    });
  }

  if (!canSplit) {
    return (
      <Page
        backTo={`/shipment/shipments/${id}`}
        title={`Split Shipment #${id}`}
      >
        <div className="m-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
          Only an effective root shipment that has not been split can be split.
        </div>
      </Page>
    );
  }

  return (
    <Page
      backTo={`/shipment/shipments/${shipment.id}`}
      title={`Split Shipment #${shipment.id}`}
      subtitle="Assign every product row once. New shipments will keep the shipment logistics data."
      rightButtons={
        <Button
          primary
          loading={splitShipmentResult.loading}
          disabled={!canSubmit}
          onClick={submit}
        >
          Create {targets.length} Shipments
        </Button>
      }
    >
      <div className="grid flex-1 gap-6 overflow-auto p-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)] lg:p-6">
        <section>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold">Products to split</h2>
            </div>
            <div className="text-sm">
              Selected{" "}
              <span className="font-bold">{selectedCbm.toFixed(3)}</span> m³
            </div>
          </div>
          <SearchBar
            containerClassName="mb-4"
            value={sourceSearchText}
            onChange={setSourceSearchText}
            placeholder="Search product number or name"
          />
          <ShipmentRows
            shipment={shipment}
            selectedRowIds={selectedRowIds}
            setSelectedRowIds={setSelectedRowIds}
            assignedRowIds={assignedRowIds}
            searchText={sourceSearchText}
          />
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold">New shipments</h2>
            </div>
            <Button onClick={addTarget}>+ Shipment</Button>
          </div>

          {targets.map((target, index) => {
            const targetRows = target.rowIds
              .map((rowId) => rowById.get(rowId))
              .filter(Boolean);
            const totalCbm = targetRows.reduce(
              (total, row) => total + Number(row.totalCbm),
              0,
            );
            const targetSearchText = targetSearchTexts[target.id] || "";
            const targetRowsExpanded = Boolean(expandedTargetIds[target.id]);
            const visibleTargetRows = targetRows.filter((row) =>
              matchesProduct(row, targetSearchText),
            );
            return (
              <article
                key={target.id}
                className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
              >
                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold">Shipment {index + 1}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-3">
                      <div className="shrink-0 whitespace-nowrap text-sm">
                        <span className="font-bold">{totalCbm.toFixed(3)}</span>{" "}
                        m³
                      </div>
                      <SearchBar
                        containerClassName="w-36 shrink-0 sm:w-48"
                        value={targetSearchText}
                        onChange={(value) => {
                          setTargetSearchTexts((current) => ({
                            ...current,
                            [target.id]: value,
                          }));
                          if (value.trim()) {
                            setExpandedTargetIds((current) => ({
                              ...current,
                              [target.id]: true,
                            }));
                          }
                        }}
                        placeholder="Search product"
                      />
                      <Button
                        className="shrink-0 text-sm"
                        icon={
                          <FiChevronRight
                            className={`transition-transform ${
                              targetRowsExpanded ? "rotate-90" : ""
                            }`}
                            size={16}
                          />
                        }
                        onClick={() => toggleTargetRows(target.id)}
                      >
                        {targetRowsExpanded ? "Hide products" : "Show products"}
                      </Button>
                    </div>
                  </div>
                  <Button
                    danger
                    disabled={targets.length === 1 || targetRows.length > 0}
                    onClick={() => removeTarget(target.id)}
                  >
                    Remove
                  </Button>
                </div>
                {targetRowsExpanded ? (
                  <div className="min-h-12 max-h-[30vh] divide-y divide-gray-100 overflow-y-auto px-4 dark:divide-gray-800">
                    {visibleTargetRows.length ? (
                      visibleTargetRows.map((row) => (
                        <div
                          key={row.id}
                          className="-mx-4 flex items-center gap-3 px-4 py-3 font-normal transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start gap-4 text-sm">
                              <span className="w-32 shrink-0 break-all font-bold">
                                {row.itemNumber}
                              </span>
                              <span className="min-w-0 break-words">
                                {row.productName}
                              </span>
                            </div>
                            <div className="mt-1 flex items-start gap-4 text-sm">
                              <span className="w-32 shrink-0">
                                <span className="font-bold">
                                  {Number(row.totalCbm).toFixed(3)}
                                </span>{" "}
                                m³
                              </span>
                              <span>QTY {row.qty}</span>
                            </div>
                          </div>
                          <Button
                            className="h-8 w-8 justify-center rounded-md p-0 text-lg hover:bg-red-50 dark:hover:bg-red-950/30"
                            danger
                            onClick={() => removeRow(target.id, row.id)}
                          >
                            ×
                          </Button>
                        </div>
                      ))
                    ) : targetRows.length ? (
                      <div className="py-4 text-sm opacity-60">
                        No matching products.
                      </div>
                    ) : (
                      <div className="py-4 text-sm opacity-60">
                        No products assigned.
                      </div>
                    )}
                  </div>
                ) : null}
                <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
                  <Button
                    disabled={selectedRowIds.length === 0}
                    onClick={() => assignSelectedRows(target.id)}
                  >
                    Assign selected ({selectedRowIds.length})
                  </Button>
                </div>
              </article>
            );
          })}
        </section>
      </div>
    </Page>
  );
}

export default SplitShipment;
